import { withAlpha } from '../model/colors';
import { resourceEpoch } from './resources';

// Layer effects (model/schema EFFECT_TYPES), painted by the one renderer so the stage, the
// thumbnails and the export agree. A layer with effects is painted alone, unrotated, into a
// canvas of its own at full resolution; inner shadows and texture work on it, glass reads
// what the page already has behind the layer, drop shadows are cast from its silhouette,
// layer blur softens the whole, and everything is drawn back with the layer's transform,
// so effects turn with the layer.
//
// Each soft pass (a shadow, the glass backdrop, a layer blur) has a canvas of its own: room
// for what it throws out, at the resolution its blur needs. A blur of SOFT device pixels
// hides the grid, so a 1000 px shadow is cheap and the layer itself stays sharp; a pass is
// never coarser than a quarter of the layer, which keeps a shadow's cut-out clean.
const FAR = 20000;
const MAX = 4096;
const SOFT = 24;
const supportsFilter = ctx => typeof ctx.filter === 'string';
const canvasOf = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

export const visibleEffects = layer => (layer.effects || []).filter(effect => effect.visible);

// How far (document px) one effect reaches past the layer's box: shadows by offset, blur
// and spread, a blur by its radius, glass by the backdrop it samples.
function reachOf(effect) {
  if (effect.type === 'drop_shadow' || effect.type === 'inner_shadow') return Math.ceil(Math.max(Math.abs(effect.x), Math.abs(effect.y)) + effect.blur * 1.5 + Math.abs(effect.spread)) + 2;
  if (effect.type === 'layer_blur') return Math.ceil(effect.radius * 1.5) + 2;
  if (effect.type === 'glass') return Math.ceil(effect.blur * 1.5) + 2;
  return 2;
}
export const effectReach = effects => effects.reduce((most, effect) => Math.max(most, reachOf(effect)), 2);

// A pass: a canvas over the layer's box plus `margin` document px each side, `s` device
// pixels per document pixel (lower for a pass blurred by `blur` document px).
function pass(box, margin, scale, blur = 0) {
  const width = box.width + margin * 2;
  const height = box.height + margin * 2;
  const soft = blur > 0 ? Math.max(scale / 4, SOFT / blur) : scale;
  const s = Math.min(scale, soft, MAX / Math.max(width, height));
  const canvas = canvasOf(Math.max(1, Math.ceil(width * s)), Math.max(1, Math.ceil(height * s)));
  return { canvas, ctx: canvas.getContext('2d'), margin, s };
}
const blank = like => { const canvas = canvasOf(like.canvas.width, like.canvas.height); return { canvas, ctx: canvas.getContext('2d'), margin: like.margin, s: like.s }; };
// Draws pass `from` onto `to`, lined up on the layer (`to` may be the page's context in layer
// units: margin 0, one pixel per document pixel).
function place(to, from) {
  const at = (to.margin - from.margin) * to.s;
  const ratio = to.s / from.s;
  to.ctx.drawImage(from.canvas, at, at, from.canvas.width * ratio, from.canvas.height * ratio);
}

// The layer's shape in black, laid out like `like`, grown (spread > 0) or shrunk (spread <
// 0) by `spread` document px: copies offset around two rings, united or intersected.
function silhouette(like, body, spread) {
  const base = blank(like);
  place(base, body);
  const radius = Math.abs(spread) * like.s;
  const out = radius >= 0.5 ? blank(like) : base;
  if (out !== base) {
    out.ctx.drawImage(base.canvas, 0, 0);
    out.ctx.globalCompositeOperation = spread > 0 ? 'source-over' : 'destination-in';
    [radius, radius / 2].forEach(ring => {
      for (let i = 0; i < 16; i += 1) {
        const angle = (i / 16) * Math.PI * 2;
        out.ctx.drawImage(base.canvas, Math.cos(angle) * ring, Math.sin(angle) * ring);
      }
    });
  }
  out.ctx.globalCompositeOperation = 'source-in';
  out.ctx.fillStyle = '#000000';
  out.ctx.fillRect(0, 0, out.canvas.width, out.canvas.height);
  out.ctx.globalCompositeOperation = 'source-over';
  return out;
}

// A shadow of `mask` onto `target` (passes laid out alike), blurred and offset (the mask
// itself is drawn far away, so only its shadow lands: works where the canvas has no `filter`).
function cast(target, mask, { color, opacity, x, y, blur }) {
  const { ctx, s } = target;
  ctx.save();
  ctx.shadowColor = withAlpha(color, opacity);
  ctx.shadowBlur = blur * s;
  ctx.shadowOffsetX = x * s + FAR;
  ctx.shadowOffsetY = y * s;
  ctx.drawImage(mask.canvas, -FAR, 0);
  ctx.restore();
}

// `source` blurred by `radius` device pixels: the canvas filter where there is one; else
// down- and up-sampling, which comes close.
function blurred(source, radius) {
  if (radius < 0.5) return source;
  const out = canvasOf(source.width, source.height);
  const ctx = out.getContext('2d');
  if (supportsFilter(ctx)) {
    ctx.filter = `blur(${radius / 2}px)`;
    ctx.drawImage(source, 0, 0);
    // Glass keeps drawing on this canvas: the mask, the paint and the letters stay sharp.
    ctx.filter = 'none';
    return out;
  }
  const factor = Math.max(1, radius / 3);
  const small = canvasOf(Math.max(1, Math.round(source.width / factor)), Math.max(1, Math.round(source.height / factor)));
  const smallCtx = small.getContext('2d');
  smallCtx.imageSmoothingQuality = 'high';
  smallCtx.drawImage(source, 0, 0, small.width, small.height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(small, 0, 0, out.width, out.height);
  return out;
}

// Inside the layer: the shadow of everything around it (worked out in a pass of its own),
// cut to the layer at the layer's resolution.
function innerShadow(body, effect, box, scale) {
  const area = pass(box, reachOf(effect), scale, effect.blur);
  const shape = silhouette(area, body, -effect.spread);
  const outside = blank(area);
  outside.ctx.fillRect(0, 0, outside.canvas.width, outside.canvas.height);
  outside.ctx.globalCompositeOperation = 'destination-out';
  outside.ctx.drawImage(shape.canvas, 0, 0);
  const shadow = blank(area);
  cast(shadow, outside, effect);
  body.ctx.save();
  body.ctx.globalCompositeOperation = 'source-atop';
  place(body, shadow);
  body.ctx.restore();
}

// Film grain: a fixed noise (seeded, so every render is the same), its grain `size`
// document pixels, blended over the layer with "overlay".
const grains = new Map();
function grain(size) {
  const cell = Math.max(1, Math.round(size));
  if (grains.has(cell)) return grains.get(cell);
  const count = Math.max(16, Math.min(128, Math.floor(512 / cell)));
  const base = canvasOf(count, count);
  const baseCtx = base.getContext('2d');
  const data = baseCtx.createImageData(count, count);
  let seed = 0x2f6e2b1;
  const random = () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = 0; i < count * count; i += 1) {
    const value = Math.round(random() * 255);
    data.data.set([value, value, value, 255], i * 4);
  }
  baseCtx.putImageData(data, 0, 0);
  const tile = canvasOf(count * cell, count * cell);
  const tileCtx = tile.getContext('2d');
  tileCtx.imageSmoothingEnabled = false;
  tileCtx.drawImage(base, 0, 0, tile.width, tile.height);
  grains.set(cell, tile);
  return tile;
}
function texture(body, { amount, size }) {
  const noise = blank(body);
  noise.ctx.fillStyle = noise.ctx.createPattern(grain(size * body.s), 'repeat');
  noise.ctx.fillRect(0, 0, noise.canvas.width, noise.canvas.height);
  noise.ctx.globalCompositeOperation = 'destination-in';
  noise.ctx.drawImage(body.canvas, 0, 0);
  body.ctx.save();
  body.ctx.globalAlpha = amount;
  body.ctx.globalCompositeOperation = 'overlay';
  body.ctx.drawImage(noise.canvas, 0, 0);
  body.ctx.restore();
}

// Frosted glass: what the page already shows behind the layer, blurred (sampled in a pass
// of its own, with room for the blur), inside the layer's silhouette; the layer's own paint
// through it at `opacity`, and a light edge.
function glass(page, surface, effect, box, scale) {
  const backdrop = pass(box, reachOf(effect), scale, effect.blur);
  if (typeof page.getTransform === 'function') {
    // This canvas's pixel → the page's device pixel, inverted to sample the page.
    backdrop.ctx.setTransform(page.getTransform().translate(-backdrop.margin, -backdrop.margin).scale(1 / backdrop.s).inverse());
    backdrop.ctx.drawImage(page.canvas, 0, 0);
    backdrop.ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  const frosted = blurred(backdrop.canvas, effect.blur * backdrop.s);
  // Past the art's edge there is nothing to sample, and a wide blur fades into it: fill the
  // fade back (each copy onto itself squares what is still clear), the averaged color kept.
  const frostedCtx = frosted.getContext('2d');
  for (let i = 0; i < 6; i += 1) frostedCtx.drawImage(frosted, 0, 0);
  const pane = blank(surface);
  place(pane, { ...backdrop, canvas: frosted });
  pane.ctx.globalCompositeOperation = 'destination-in';
  pane.ctx.drawImage(surface.canvas, 0, 0);
  pane.ctx.globalCompositeOperation = 'source-over';
  pane.ctx.globalAlpha = effect.opacity;
  pane.ctx.drawImage(surface.canvas, 0, 0);
  pane.ctx.globalAlpha = 1;
  if (effect.edge > 0) innerShadow(pane, { color: '#ffffff', opacity: effect.edge, x: 0, y: 2, blur: 4, spread: 0 }, box, scale);
  return pane;
}

// Finished effect renders (the passes to draw) are kept per layer (layers are immutable)
// and scale, until an image loads. Glass is never kept: it depends on what is behind.
const kept = new WeakMap();
function keptRender(layer, key) { return kept.get(layer)?.get(key) || null; }
function keep(layer, key, value) {
  const entries = kept.get(layer) || new Map();
  if (entries.size > 3) entries.delete(entries.keys().next().value);
  entries.set(key, value);
  kept.set(layer, entries);
}

// Paints `layer` with its effects on `ctx`, already in the layer's local coordinates.
// `paintBody(ctx, scale, part)` paints the layer itself at the origin; a `layered` one (a
// text on a background box) also paints just its 'surface' or just its 'ink'.
export function drawWithEffects(ctx, layer, height, effects, scale, paintBody, { layered = false } = {}) {
  const page = { ctx, margin: 0, s: 1 };
  // Grouped (one canvas, drawn once) when the layer is see-through, so its opacity applies
  // to the layer and its shadows together.
  const group = ctx.globalAlpha < 1;
  const key = `${scale}|${group}|${resourceEpoch()}`;
  // Glass reads what is behind, and a video's frame moves: neither is kept.
  const withGlass = effects.some(effect => effect.type === 'glass') || layer.type === 'video';
  const cached = !withGlass && keptRender(layer, key);
  if (cached) { cached.forEach(piece => place(page, piece)); return; }
  const box = { width: layer.width, height };
  // The body's room: what letters (tight line heights) or a line's stroke may paint past the box.
  const bleed = layer.type === 'text' ? Math.ceil((layer.style?.font_size || 0) / 2) : Math.ceil((layer.stroke?.width || 0) / 2);
  const whole = pass(box, bleed + 2, scale);
  if (!whole.ctx) { paintBody(ctx, scale); return; }
  // The layer (or a part of it) in layer units; the effects then work in canvas pixels.
  const paintInto = (target, part) => {
    target.ctx.scale(target.s, target.s);
    target.ctx.translate(target.margin, target.margin);
    paintBody(target.ctx, target.s, part);
    target.ctx.setTransform(1, 0, 0, 1, 0, 0);
    return target;
  };
  paintInto(whole);
  const glassEffect = effects.find(effect => effect.type === 'glass');
  // Glass on a text with a background box: the box is the glass and the letters stay whole
  // on top (Figma's frame and the text in it). Shadows still come from the whole layer.
  const split = Boolean(glassEffect && layered);
  const surface = split ? paintInto(blank(whole), 'surface') : whole;
  const ink = split ? paintInto(blank(whole), 'ink') : null;
  effects.filter(effect => effect.type === 'inner_shadow').forEach(effect => innerShadow(surface, effect, box, scale));
  effects.filter(effect => effect.type === 'texture').forEach(effect => [surface, ink].filter(Boolean).forEach(target => texture(target, effect)));
  const body = glassEffect ? glass(ctx, surface, glassEffect, box, scale) : surface;
  if (ink) body.ctx.drawImage(ink.canvas, 0, 0);
  let pieces = effects.filter(effect => effect.type === 'drop_shadow').map(effect => {
    const shadow = pass(box, reachOf(effect), scale, effect.blur);
    cast(shadow, silhouette(shadow, whole, effect.spread), effect);
    // Off "behind transparent areas": the layer's own area stays clear of its shadow.
    if (!effect.behind) { shadow.ctx.globalCompositeOperation = 'destination-out'; place(shadow, whole); shadow.ctx.globalCompositeOperation = 'source-over'; }
    return shadow;
  });
  pieces.push(body);
  const blurs = effects.filter(effect => effect.type === 'layer_blur');
  if (blurs.length || (group && pieces.length > 1)) {
    const all = pass(box, effectReach(effects), scale, blurs.length ? Math.max(...blurs.map(effect => effect.radius)) : 0);
    pieces.forEach(piece => place(all, piece));
    pieces = [{ ...all, canvas: blurs.reduce((canvas, effect) => blurred(canvas, effect.radius * all.s), all.canvas) }];
  }
  if (!withGlass) keep(layer, key, pieces);
  pieces.forEach(piece => place(page, piece));
}
